/**
 * Purchase Order API helpers
 */
import apiClient from './client';

/** Reusable paginated response shape (backend app/core/pagination) */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// TypeScript interfaces matching backend PurchaseOrderRead schema
export interface PurchaseOrder {
  id: number;
  po_code: string;
  po_title?: string | null;
  po_excel_id?: string | null;
  client_name: string | null;
  project_name: string | null;
  project_type: string | null;
  geography: string | null;
  account_manager: string | null;
  project_manager: string | null;
  linked_msa: string | null;
  revenue_type: string | null;
  revenue_class: string | null;
  po_date: string | null; // ISO date string 'YYYY-MM-DD'
  po_value_original: number | null; // Current PO Value (updated after cancellations, used for calculations)
  po_value_initial: number | null; // Original PO Value (never changes, preserved for reference)
  currency: string | null;
  fx_rate_usd: number | null;
  fx_rate_inr: number | null;
  payment_terms: string | null;
  status: string | null;
  remarks: string | null;
  cancellation_events: POCancellationEvent[] | null;
  created_at: string; // ISO datetime string
  updated_at: string | null; // ISO datetime string
}

export interface POCancellationEvent {
  id: string;
  date: string;
  new_value: number;
  change_amount: number;
  change_percentage: number;
  description?: string | null;
}

export interface PurchaseOrderCreateInput {
  client_name?: string | null;
  project_name?: string | null;
  project_type?: string | null;
  geography?: string | null;
  account_manager?: string | null;
  project_manager?: string | null;
  linked_msa?: string | null;
  revenue_type?: string | null;
  revenue_class?: string | null;
  po_date?: string | null; // ISO date 'YYYY-MM-DD'
  po_value_original?: number | null;
  po_value_initial?: number | null;
  currency?: string | null;
  fx_rate_usd?: number | null;
  fx_rate_inr?: number | null;
  payment_terms?: string | null;
  status?: string | null; // default to "Active"
  remarks?: string | null;
}

export interface PurchaseOrderUpdateInput {
  client_name?: string | null;
  project_name?: string | null;
  project_type?: string | null;
  geography?: string | null;
  account_manager?: string | null;
  project_manager?: string | null;
  linked_msa?: string | null;
  revenue_type?: string | null;
  revenue_class?: string | null;
  po_date?: string | null;
  po_value_original?: number | null;
  po_value_initial?: number | null;
  currency?: string | null;
  fx_rate_usd?: number | null;
  fx_rate_inr?: number | null;
  payment_terms?: string | null;
  status?: string | null;
  remarks?: string | null;
  cancellation_events?: POCancellationEvent[] | null;
}

export async function deliveryPos(): Promise<PurchaseOrder[]> {
  const response = await apiClient.get<PurchaseOrder[]>('/api/v1/delivery/po-master');
  return response.data;
}




export interface POPaginationParams {
  status?: string;
  client_name?: string;
  project_name?: string;
  page?: number;
  page_size?: number;
}

export interface POExportFilterParams {
  status?: string;
  client_name?: string;
  project_name?: string;
}

/**
 * Fetch purchase orders (paginated)
 */
export async function fetchPurchaseOrders(
  params?: POPaginationParams
): Promise<PaginatedResponse<PurchaseOrder>> {
  const response = await apiClient.get<PaginatedResponse<PurchaseOrder> & { pos?: PurchaseOrder[] }>(
    '/api/v1/po/get_all_pos',
    { params }
  );
  const data = response.data;
  const items = Array.isArray(data?.pos) ? data.pos : Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: data?.total ?? items.length,
    page: data?.page ?? 1,
    page_size: data?.page_size ?? (items.length || 20),
    total_pages: data?.total_pages ?? 1,
  };
}

/**
 * Fetch full purchase order list from export endpoint (no pagination).
 */
export async function fetchPOFullList(
  params?: POExportFilterParams,
): Promise<PurchaseOrder[]> {
  const response = await apiClient.get<
    | PurchaseOrder[]
    | PaginatedResponse<PurchaseOrder>
    | { items?: PurchaseOrder[]; pos?: PurchaseOrder[] }
  >("/api/v1/po/get_all_pos", {
    params,
  });

  const data = response.data as
    | PurchaseOrder[]
    | PaginatedResponse<PurchaseOrder>
    | { items?: PurchaseOrder[]; pos?: PurchaseOrder[] };

  if (Array.isArray(data)) return data;
  if (Array.isArray((data as { pos?: PurchaseOrder[] }).pos)) {
    return (data as { pos?: PurchaseOrder[] }).pos ?? [];
  }
  if (Array.isArray((data as { items?: PurchaseOrder[] }).items)) {
    return (data as { items?: PurchaseOrder[] }).items ?? [];
  }
  return [];
}

/**
 * Get a single purchase order by ID
 */
export async function getPurchaseOrder(poId: number): Promise<PurchaseOrder> {
  const response = await apiClient.get<{ po: PurchaseOrder }>(`/api/v1/po/${poId}`);
  return response.data?.po ?? (response.data as unknown as PurchaseOrder);
}

/**
 * Create a new purchase order
 */
export async function createPurchaseOrder(data: PurchaseOrderCreateInput): Promise<PurchaseOrder> {
  const response = await apiClient.post<PurchaseOrder>('/api/v1/po/', data);
  return response.data;
}

/**
 * Update a purchase order
 */
export async function updatePurchaseOrder(poId: number, data: PurchaseOrderUpdateInput): Promise<PurchaseOrder> {
  const response = await apiClient.put<PurchaseOrder>(`/api/v1/po/${poId}`, data);
  return response.data;
}

// Invoice interfaces
export interface Invoice {
  id: number;
  diff_finance_planned_date?: number | null;
  invoice_code: string;
  po_id: number;
  client_name: string | null;
  project_name: string | null;
  description: string | null;
  est_value_original: number | null;
  currency: string | null;
  fx_rate_usd: number | null;
  fx_rate_inr: number | null;
  finance_planned_date: string | null;
  operational_planned_date: string | null;
  service_month_date: string | null;
  operational_revenue_value: number | null;
  status: string | null;
  invoice_date: string | null;
  invoiced_value_original: number | null;
  payment_terms: string | null;
  payment_received_date: string | null;
  payment_received_value: number | null;
  remarks: string | null;
  days_delayed?: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface InvoiceCreateInput {
  invoice_code?: string; // Optional - backend will auto-generate if not provided
  po_id: number;
  description?: string | null;
  est_value_original?: number | null;
  currency?: string | null;
  fx_rate_usd?: number | null;
  fx_rate_inr?: number | null;
  finance_planned_date?: string | null;
  operational_planned_date?: string | null;
  service_month_date?: string | null;
  operational_revenue_value?: number | null;
  status?: string; // we will default to "Planned"
  invoice_date?: string | null;
  invoiced_value_original?: number | null;
  payment_terms?: string | null;
  payment_received_date?: string | null;
  payment_received_value?: number | null;
  remarks?: string | null;
}

/**
 * Fetch all invoices for a purchase order
 */
export async function fetchInvoicesForPO(poId: number): Promise<Invoice[]> {
  const response = await apiClient.get<Invoice[]>(`/api/v1/po/${poId}/invoices`);
  return response.data;
}

/**
 * Create an invoice for a purchase order
 */
export async function createInvoiceForPO(poId: number, data: InvoiceCreateInput | InvoiceCreateInput[]): Promise<Invoice | Invoice[]> {
  const payload = Array.isArray(data) ? { invoices: data } : data;
  const response = await apiClient.post<Invoice | Invoice[]>(`/api/v1/po/${poId}/invoices`, payload);
  return response.data;
}

export interface InvoiceUpdateInput {
  status?: string;
  finance_planned_date?: string | null;
  operational_planned_date?: string | null;
  service_month_date?: string | null;
  invoice_date?: string | null;
  est_value_original?: number | null;
  invoiced_value_original?: number | null;
  fx_rate_usd?: number | null;
  fx_rate_inr?: number | null;
  payment_terms?: string | null;
  payment_received_date?: string | null;
  payment_received_value?: number | null;
  remarks?: string | null;
}

export interface InvoiceFilterParams {
  status?: string;
  client_name?: string;
  project_name?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}

export interface InvoiceExportFilterParams {
  status?: string;
  client_name?: string;
  project_name?: string;
  from_date?: string;
  to_date?: string;
}

/**
 * Fetch invoices with optional filters (paginated)
 */
export async function fetchInvoices(
  params?: InvoiceFilterParams
): Promise<PaginatedResponse<Invoice>> {
  const response = await apiClient.get<PaginatedResponse<Invoice>>('/api/v1/invoices/get_all_invoices', {
    params,
  });
  const data = response.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: data?.total ?? items.length,
    page: data?.page ?? 1,
    page_size: data?.page_size ?? (items.length || 20),
    total_pages: data?.total_pages ?? 1,
  };
}

/**
 * Fetch full invoice list from export endpoint.
 * Uses /api/v1/invoices (without trailing slash) as requested.
 */
export async function fetchInvoicesFullList(
  params?: InvoiceExportFilterParams,
): Promise<Invoice[]> {
  const response = await apiClient.get<
    Invoice[] | PaginatedResponse<Invoice> | { items?: Invoice[]; invoices?: Invoice[] }
  >("/api/v1/invoices/get_all_invoices", {
    params,
  });

  const data = response.data as
    | Invoice[]
    | PaginatedResponse<Invoice>
    | { items?: Invoice[]; invoices?: Invoice[] };

  if (Array.isArray(data)) return data;
  if (Array.isArray((data as { invoices?: Invoice[] }).invoices)) {
    return (data as { invoices?: Invoice[] }).invoices ?? [];
  }
  if (Array.isArray((data as { items?: Invoice[] }).items)) {
    return (data as { items?: Invoice[] }).items ?? [];
  }
  return [];
}

/**
 * Get a single invoice by ID
 */
export async function getInvoice(invoiceId: number): Promise<Invoice> {
  const response = await apiClient.get<Invoice>(`/api/v1/invoices/${invoiceId}`);
  return response.data;
}

// delete invoice by id


/**
 * Update an invoice
 */
export async function updateInvoice(invoiceId: number, data: InvoiceUpdateInput): Promise<Invoice> {
  const response = await apiClient.put<Invoice>(`/api/v1/invoices/${invoiceId}`, data);
  return response.data;
}

/**
 * Delete a purchase order
 */
export async function deletePurchaseOrder(poId: number): Promise<void> {
  await apiClient.delete(`/api/v1/po/${poId}`);
}

/**
 * Delete an invoice
 */
export async function deleteInvoice(invoiceId: number): Promise<void> {
  await apiClient.delete(`/api/v1/invoices/${invoiceId}`);
}

/**
 * Fetch FX rates for a base currency
 */
export async function fetchFxRates(baseCurrency: string): Promise<{ fx_rate_usd: number | null; fx_rate_inr: number | null }> {
  const response = await apiClient.get<{ fx_rate_usd: number | null; fx_rate_inr: number | null }>(
    `/api/v1/po/fx-rates?base_currency=${encodeURIComponent(baseCurrency)}`
  );
  return response.data;
}

export interface POProjectsResponse {
  project_names: string[];
}

/**
 * Fetch distinct project names for PO filters/forms.
 * When `clientName` is set, returns projects for that client only.
 */
export async function fetchPOProjectNames(
  clientName?: string | null
): Promise<string[]> {
  const trimmed =
    typeof clientName === "string" && clientName.trim()
      ? clientName.trim()
      : undefined;
  const response = await apiClient.get<POProjectsResponse>(
    "/api/v1/po/po_projects",
    { params: trimmed ? { client_name: trimmed } : undefined }
  );
  const names = response.data?.project_names;
  return Array.isArray(names) ? names : [];
}

export interface POClientNamesResponse {
  client_names: string[];
}

/**
 * Fetch distinct client names for PO filters/forms.
 * When `projectName` is set, returns clients that have that project.
 */
export async function fetchPOClientNames(
  projectName?: string | null
): Promise<string[]> {
  const trimmed =
    typeof projectName === "string" && projectName.trim()
      ? projectName.trim()
      : undefined;
  const response = await apiClient.get<POClientNamesResponse>(
    "/api/v1/po/po_client_names",
    { params: trimmed ? { project_name: trimmed } : undefined }
  );
  const names = response.data?.client_names;
  return Array.isArray(names) ? names : [];
}

export interface AccountManagersResponse {
  account_managers: string[];
}

export async function fetchAccountManagers(): Promise<string[]> {
  const response = await apiClient.get<AccountManagersResponse>(
    "/api/v1/po/account_managers"
  );
  const names = response.data?.account_managers;
  return Array.isArray(names) ? names : [];
}

export interface ProjectManagersResponse {
  project_managers: string[];
}

export async function fetchProjectManagers(): Promise<string[]> {
  const response = await apiClient.get<ProjectManagersResponse>(
    "/api/v1/po/project_managers"
  );
  const names = response.data?.project_managers;
  return Array.isArray(names) ? names : [];
}
